import { useEffect, useState } from 'react';
import { Layout } from '../components/Layout';
import { CurrencySelector } from '../components/CurrencySelector';
import { PeriodFilter, type DateRange } from '../components/PeriodFilter';
import { SalesFilterBar } from '../components/SalesFilterBar';
import { SalesChart } from '../components/SalesChart';
import { SalesTable } from '../components/SalesTable';
import { StatCard } from '../components/StatCard';
import {
  ApiError,
  fetchMyPayments,
  fetchMySales,
  fetchMySummary,
  type AffiliatePaymentsSummary,
  type SaleRow,
  type SaleSummary,
} from '../lib/api';
import { formatCurrency, formatDateTime, pickCurrency } from '../lib/format';
import { COMMISSION_GENERATING_STATUSES } from '../lib/statusLabels';
import { useAffiliateSession } from '../contexts/AffiliateSessionContext';

export function InfluencerDashboardPage() {
  const { logout, invalidate } = useAffiliateSession();
  const [affiliateName, setAffiliateName] = useState('');
  const [couponCodes, setCouponCodes] = useState<string[]>([]);

  const [range, setRange] = useState<DateRange>({});
  const [status, setStatus] = useState('');
  const [search, setSearch] = useState('');
  const [hideCanceled, setHideCanceled] = useState(false);
  const [page, setPage] = useState(1);
  const [currency, setCurrency] = useState('BRL');

  const [summary, setSummary] = useState<SaleSummary | null>(null);
  const [sales, setSales] = useState<{ rows: SaleRow[]; hasMore: boolean }>({ rows: [], hasMore: false });
  const [paymentsSummary, setPaymentsSummary] = useState<AffiliatePaymentsSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // "A receber"/"Pago" e o histórico de pagamentos são sobre o TOTAL
  // ganho (nunca uma janela de datas) — busca uma vez só, independente do
  // filtro de período das vendas.
  useEffect(() => {
    let cancelled = false;
    fetchMyPayments()
      .then((result) => {
        if (!cancelled) setPaymentsSummary(result);
      })
      .catch((err) => {
        if (cancelled) return;
        if (err instanceof ApiError && (err.status === 401 || err.code === 'unauthorized')) {
          invalidate();
        }
        // Falha aqui não deve travar o resto do dashboard — os cards
        // ficam em 'N/D' e a aba de pagamentos fica vazia.
      });
    return () => {
      cancelled = true;
    };
  }, [invalidate]);

  useEffect(() => {
    setPage(1);
  }, [range.from, range.to, status, search]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    Promise.all([
      fetchMySummary(range),
      fetchMySales({ from: range.from, to: range.to, status: status || undefined, search: search || undefined, page }),
    ])
      .then(([summaryResult, salesResult]) => {
        if (cancelled) return;
        setSummary(summaryResult.summary);
        setAffiliateName(summaryResult.affiliateName);
        setCouponCodes(summaryResult.couponCodes);
        setSales(salesResult);
      })
      .catch((err) => {
        if (cancelled) return;
        // Sessão expirada/revogada (ex.: o admin desativou o afiliado
        // enquanto ele estava logado) — nunca trava numa tela de erro,
        // volta pra tela de consulta.
        if (err instanceof ApiError && (err.status === 401 || err.code === 'unauthorized')) {
          invalidate();
          return;
        }
        setError(err instanceof Error ? err.message : 'Erro ao carregar os dados.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [range, status, search, page, invalidate]);

  // A lista de moedas disponíveis vem só de quem tem venda de verdade no
  // período (summary.byCurrency, já ordenado com BRL primeiro pelo
  // Worker) — nunca a lista inteira de moedas ISO. Se a moeda selecionada
  // deixar de existir nessa lista (trocou o período, ou é a primeira
  // carga), cai pra BRL quando disponível, senão a primeira da lista.
  const availableCurrencies = summary?.byCurrency.map((entry) => entry.currency) ?? [];
  useEffect(() => {
    if (availableCurrencies.length === 0) return;
    if (!availableCurrencies.includes(currency)) {
      setCurrency(availableCurrencies[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availableCurrencies.join(',')]);

  const currencyBucket = pickCurrency(summary?.byCurrency, currency);
  const paymentsBucket = pickCurrency(paymentsSummary?.byCurrency, currency);
  const chartSeries = summary?.seriesByCurrency.find((entry) => entry.currency === currency)?.series ?? [];
  const visibleSalesRows = hideCanceled ? sales.rows.filter((row) => COMMISSION_GENERATING_STATUSES.has(row.status)) : sales.rows;

  return (
    <Layout onSignOut={() => logout()}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="font-display text-2xl font-bold tracking-tight">Olá, {affiliateName || '...'} 👋</h1>
        <CurrencySelector currencies={availableCurrencies} value={currency} onChange={setCurrency} />
      </div>
      <p className="mt-1 text-sm text-[rgb(var(--text-muted))]">Acompanhe suas vendas do CA Render em tempo real.</p>

      {couponCodes.length > 0 ? (
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <span className="text-xs uppercase tracking-wide text-[rgb(var(--text-muted))]">
            {couponCodes.length > 1 ? 'Seus cupons ativos' : 'Seu cupom ativo'}
          </span>
          {couponCodes.map((code) => (
            <span
              key={code}
              className="border border-[rgb(var(--border))] px-2.5 py-1 font-mono text-xs text-[rgb(var(--text-muted))]"
            >
              {code}
            </span>
          ))}
        </div>
      ) : null}

      {summary?.commissionTierStatus ? (
        <div className="card mt-4 p-4">
          <p className="text-sm">
            Este mês você fez <strong>{summary.commissionTierStatus.salesCountThisMonth}</strong> venda
            {summary.commissionTierStatus.salesCountThisMonth === 1 ? '' : 's'} · faixa atual{' '}
            <strong>{summary.commissionTierStatus.tierLabel}</strong> · comissão de{' '}
            <strong>{summary.commissionTierStatus.tierPercent}%</strong>.
          </p>
          {summary.commissionTierStatus.nextTierLabel ? (
            <p className="mt-1 text-sm text-[rgb(var(--text-muted))]">
              Faltam <strong>{summary.commissionTierStatus.salesUntilNextTier}</strong> venda
              {summary.commissionTierStatus.salesUntilNextTier === 1 ? '' : 's'} para a faixa{' '}
              {summary.commissionTierStatus.nextTierLabel} ({summary.commissionTierStatus.nextTierPercent}%).
            </p>
          ) : (
            <p className="mt-1 text-sm text-[rgb(var(--text-muted))]">Você já está na faixa máxima de comissão.</p>
          )}
        </div>
      ) : null}

      <div className="mt-6">
        <PeriodFilter onChange={setRange} />
      </div>

      {error ? <p className="mt-4 text-sm text-error">{error}</p> : null}

      <div className="mt-4 grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatCard label="Vendas" value={currencyBucket ? String(currencyBucket.salesCount) : 'N/D'} />
        <StatCard
          label="Sua comissão"
          value={currencyBucket ? formatCurrency(currencyBucket.commission, currency) : 'N/D'}
          tone="positive"
        />
        <StatCard
          label="A receber"
          value={paymentsSummary ? formatCurrency(paymentsBucket?.pending ?? 0, currency) : 'N/D'}
          tone="positive"
        />
        <StatCard label="Pago" value={paymentsSummary ? formatCurrency(paymentsBucket?.paid ?? 0, currency) : 'N/D'} />
      </div>

      <div className="mt-6">
        <SalesChart series={chartSeries} currency={currency} showRevenue={false} />
      </div>

      <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <h2 className="font-display text-lg font-semibold tracking-tight">Vendas</h2>
        <SalesFilterBar
          status={status}
          search={search}
          onStatusChange={setStatus}
          onSearchChange={setSearch}
          hideCanceled={hideCanceled}
          onHideCanceledChange={setHideCanceled}
        />
      </div>

      <div className="mt-3">
        <SalesTable rows={visibleSalesRows} loading={loading} page={page} hasMore={sales.hasMore} onPageChange={setPage} />
      </div>

      <h2 className="mt-8 font-display text-lg font-semibold tracking-tight">Pagamentos</h2>
      <p className="mt-1 text-sm text-[rgb(var(--text-muted))]">Histórico de quando sua comissão foi paga.</p>
      <div className="mt-3 card overflow-hidden">
        {paymentsSummary && paymentsSummary.payments.length > 0 ? (
          <div className="divide-y divide-[rgb(var(--border))]">
            {paymentsSummary.payments.map((payment) => (
              <div key={payment.id} className="flex items-center justify-between gap-3 p-4">
                <div>
                  <p className="text-sm">{formatDateTime(payment.paidAt)}</p>
                  {payment.note ? <p className="text-xs text-[rgb(var(--text-muted))]">{payment.note}</p> : null}
                </div>
                <p className="font-mono text-sm font-medium text-success">{formatCurrency(payment.amount, payment.currency)}</p>
              </div>
            ))}
          </div>
        ) : (
          <p className="p-8 text-center text-sm text-[rgb(var(--text-muted))]">Nenhum pagamento registrado ainda.</p>
        )}
      </div>
    </Layout>
  );
}
